/**
 * useStripeFeeConfig — React Query hooks for the platform Stripe fee
 * config singleton.
 *
 * Endpoints under /api/platform:
 *   GET   /stripe-fee-config — any authenticated user
 *   PATCH /stripe-fee-config — super admin only (enforced server-side)
 *
 * The GET is used by the event creation/edit form to render a live
 * transaction-fee preview as the organizer types the entry fee. The
 * PATCH powers the inline super-admin edit pencil that opens the fee
 * config modal.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

export interface StripeFeeConfig {
  percentage_rate: number;
  fixed_fee_cents: number;
  enabled: boolean;
  currency: string;
  updated_by: string | null;
  updated_at: string;
}

export interface StripeFeeConfigUpdate {
  percentage_rate?: number;
  fixed_fee_cents?: number;
  enabled?: boolean;
  currency?: string;
}

export const stripeFeeConfigKeys = {
  all: ['stripe-fee-config'] as const,
  config: () => [...stripeFeeConfigKeys.all, 'singleton'] as const,
};

/**
 * Load the platform Stripe fee config. Cached for 5 minutes since the
 * value rarely changes; mutations invalidate explicitly.
 */
export function useStripeFeeConfig() {
  return useQuery({
    queryKey: stripeFeeConfigKeys.config(),
    queryFn: async () => {
      const res = await httpClient.get<{ data: StripeFeeConfig; success: boolean }>(
        '/platform/stripe-fee-config',
      );
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Super-admin-only mutation to update the platform fee config. The
 * server enforces the role check; the UI hides the pencil for non-super
 * admins so non-super admins won't reach this hook in practice.
 */
export function useUpdateStripeFeeConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: StripeFeeConfigUpdate) => {
      const res = await httpClient.patch<{ data: StripeFeeConfig; success: boolean }>(
        '/platform/stripe-fee-config',
        updates,
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(stripeFeeConfigKeys.config(), data);
    },
  });
}
